/**
 * Forgot Password — email-entry page.
 * Always shows the same "if that email is on file..." confirmation regardless
 * of whether the address exists, mirroring the backend's anti-enumeration
 * guarantee.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSubmitted(true);
      toast.success('Check your inbox for the reset link');
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        toast.error('Too many requests. Please wait a minute and try again.', { duration: 6000 });
      } else {
        // Even on transport errors we show the success state — same anti-enum
        // posture as the backend. The user can always retry.
        setSubmitted(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0d0b] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md">
        <div className="glass-card p-8 md:p-10" data-testid="forgot-password-card">
          <Link to="/login" className="inline-flex items-center text-[#8a948a] hover:text-[#c8b273] text-sm mb-6" data-testid="back-to-login-link">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to sign in
          </Link>

          <h1 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-extrabold uppercase tracking-wider text-white mb-3">
            Reset your password
          </h1>

          {!submitted ? (
            <>
              <p className="text-[#a8b0a8] text-sm mb-6 leading-relaxed">
                Enter the email you signed up with and we'll send you a link to choose a new password. The link is valid for 30 minutes.
              </p>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-form">
                <div>
                  <label className="block text-xs uppercase tracking-wider text-[#8a948a] mb-2">Email</label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    autoFocus
                    autoComplete="email"
                    inputMode="email"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="btn-gold w-full"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? 'Sending...' : 'Send reset link'}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-4" data-testid="forgot-success">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#c8b273]/15 border border-[#c8b273]/40 mb-4">
                <Mail className="w-6 h-6 text-[#c8b273]" />
              </div>
              <p className="text-white font-semibold mb-2">Check your inbox</p>
              <p className="text-[#a8b0a8] text-sm leading-relaxed">
                If <span className="text-[#c8b273]">{email}</span> is on file, a reset link is on its way. The email may take a minute to arrive — check your spam folder if you don't see it.
              </p>
              <Link to="/login" className="inline-block mt-6 text-[#c8b273] hover:underline text-sm">
                Return to sign in
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
